/**
 * A1 range grammar, the one copy shared by the op schema (`ops.ts`) and the
 * backend's spreadsheet read and write paths.
 */
/** Zero-based row and column to an A1 address: (0, 0) is `A1`, (14, 27) is `AB15`. */
export function toA1(row, column) {
    let letters = '';
    for (let c = column + 1; c > 0; c = Math.floor((c - 1) / 26)) {
        letters = String.fromCharCode(65 + ((c - 1) % 26)) + letters;
    }
    return `${letters}${row + 1}`;
}
/** The last column (`XFD`) and row of an Excel-sized sheet, zero-based. */
const MAX_COLUMN = 16_383;
const MAX_ROW = 1_048_575;
const column = (letters) => [...letters].reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0) - 1;
/** One end of a range: a cell (`B2`), a column (`B`) or a row (`2`). */
function endpoint(text) {
    const [, letters = '', digits = ''] = /^([A-Z]*)([0-9]*)$/.exec(text) ?? [];
    if (letters.length > 3 || digits.length > 7 || digits.startsWith('0'))
        return null;
    if (!letters && !digits)
        return null;
    return {
        column: letters ? column(letters) : null,
        row: digits ? Number(digits) - 1 : null,
    };
}
const span = (x, y) => x === null || y === null ? null : [Math.min(x, y), Math.max(x, y)];
/**
 * `B2`, `B2:C4`, `B:D` or `3:5` to bounds, either corner first; null for
 * anything else, including a sheet-qualified range or one past `XFD1048576`.
 */
export function parseA1Range(text) {
    const parts = text.trim().toUpperCase().replace(/\$/g, '').split(':');
    const a = endpoint(parts[0]);
    const b = endpoint(parts[1] ?? parts[0]);
    if (parts.length > 2 || !a || !b)
        return null;
    const rows = span(a.row, b.row);
    const columns = span(a.column, b.column);
    // Both ends the same shape, and a lone column or row is not a range.
    const sameShape = (a.row === null) === (b.row === null) && (a.column === null) === (b.column === null);
    if (!sameShape || (parts.length === 1 && (!rows || !columns)))
        return null;
    if ((rows?.[1] ?? 0) > MAX_ROW || (columns?.[1] ?? 0) > MAX_COLUMN)
        return null;
    return {
        startRow: rows?.[0] ?? 0,
        endRow: rows?.[1] ?? null,
        startColumn: columns?.[0] ?? 0,
        endColumn: columns?.[1] ?? null,
    };
}
//# sourceMappingURL=a1.js.map